import React, { useState } from "react";
import { leadsAPI } from "../../services/api";
import "./Forms.css";

const formatBirthDateInput = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const formatNationalId = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 1) return digits;
  const base = digits.slice(0, -1);
  const checkDigit = digits.slice(-1);
  const baseFormatted = base.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${baseFormatted}-${checkDigit}`;
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
};

const validateUruguayanNationalId = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 8) return false;

  const normalized = digits.padStart(8, "0");
  const body = normalized.slice(0, 7).split("").map(Number);
  const checkDigit = Number(normalized.slice(7));
  const weights = [2, 9, 8, 7, 6, 3, 4];
  const sum = body.reduce((acc, digit, index) => acc + digit * weights[index], 0);
  const expectedCheckDigit = (10 - (sum % 10)) % 10;
  return checkDigit === expectedCheckDigit;
};

const isAdultByBirthDate = (value) => {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;

  const [, dayText, monthText, yearText] = match;
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const birthDate = new Date(year, month - 1, day);

  const isValidDate =
    birthDate.getFullYear() === year &&
    birthDate.getMonth() === month - 1 &&
    birthDate.getDate() === day;
  if (!isValidDate) return false;

  const today = new Date();
  let age = today.getFullYear() - year;
  const birthdayNotReached =
    today.getMonth() < month - 1 ||
    (today.getMonth() === month - 1 && today.getDate() < day);

  if (birthdayNotReached) {
    age -= 1;
  }

  return age >= 18;
};

const validateCompanyRut = (value) => {
  const digits = value.replace(/\D/g, "");
  return digits.length === 12;
};

const LoanForm = () => {
  const TOTAL_STEPS = 3;
  const [formData, setFormData] = useState({
    loanAmount: "",
    nationalId: "",
    firstName: "",
    lastName: "",
    birthDate: "",
    phone: "",
    email: "",
    gender: "",
    companyLegalName: "",
    companyTradeName: "",
    companyRut: "",
    companyAntiquity: "",
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "birthDate") {
      setFormData((prev) => ({ ...prev, birthDate: formatBirthDateInput(value) }));
      return;
    }

    if (name === "phone") {
      setFormData((prev) => ({ ...prev, phone: formatPhone(value) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNationalIdBlur = () => {
    setFormData((prev) => ({ ...prev, nationalId: formatNationalId(prev.nationalId) }));
  };

  const handleCompanyRutBlur = () => {
    const digits = formData.companyRut.replace(/\D/g, "").slice(0, 12);
    if (!digits) return;

    const formatted = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 11)}-${digits.slice(11)}`;
    setFormData((prev) => ({ ...prev, companyRut: formatted }));
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.loanAmount || Number(formData.loanAmount) < 100) {
        setSubmitMessage("error:Ingresá un monto válido (mínimo 100).");
        return false;
      }

      if (!validateUruguayanNationalId(formData.nationalId)) {
        setSubmitMessage("error:Cédula inválida. Verificá formato y dígito verificador.");
        return false;
      }

      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setSubmitMessage("error:Completá nombre y apellido.");
        return false;
      }

      if (!isAdultByBirthDate(formData.birthDate)) {
        setSubmitMessage("error:La fecha de nacimiento es inválida o la persona es menor de edad.");
        return false;
      }
    }

    if (step === 2) {
      const phoneDigits = formData.phone.replace(/\D/g, "");
      if (phoneDigits.length < 8 || phoneDigits.length > 9) {
        setSubmitMessage("error:El celular debe tener entre 8 y 9 dígitos.");
        return false;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setSubmitMessage("error:Ingresá un email válido.");
        return false;
      }

      if (!formData.gender) {
        setSubmitMessage("error:Seleccioná sexo.");
        return false;
      }
    }

    if (step === 3) {
      if (!formData.companyRut && !formData.companyAntiquity) {
        setSubmitMessage("error:Completá RUT de la empresa o Antigüedad de la empresa.");
        return false;
      }

      if (formData.companyRut && !validateCompanyRut(formData.companyRut)) {
        setSubmitMessage("error:El RUT debe tener 12 dígitos.");
        return false;
      }
    }

    return true;
  };

  const handleNextStep = () => {
    setSubmitMessage("");
    if (!validateStep(currentStep)) {
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const handlePreviousStep = () => {
    setSubmitMessage("");
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage("");

    if (!validateStep(1)) {
      setCurrentStep(1);
      return;
    }
    if (!validateStep(2)) {
      setCurrentStep(2);
      return;
    }
    if (!validateStep(3)) {
      setCurrentStep(3);
      return;
    }

    setIsSubmitting(true);
    const phoneDigits = formData.phone.replace(/\D/g, "");

    try {
      await leadsAPI.submitLoanApplication({
        ...formData,
        phone: `+598 ${phoneDigits}`,
      });
      setSubmitMessage("success:Solicitud enviada exitosamente. Nos comunicaremos pronto.");
      setFormData({
        loanAmount: "",
        nationalId: "",
        firstName: "",
        lastName: "",
        birthDate: "",
        phone: "",
        email: "",
        gender: "",
        companyLegalName: "",
        companyTradeName: "",
        companyRut: "",
        companyAntiquity: "",
      });
      setCurrentStep(1);
    } catch (error) {
      setSubmitMessage("error:Error al enviar la solicitud. Intente de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <h3>Solicitud de Préstamo</h3>
      <p className="form-subtitle">
        Paso {currentStep} de {TOTAL_STEPS}
      </p>
      <div className="stepper">
        <div className={`stepper-item ${currentStep >= 1 ? "active" : ""}`}>1</div>
        <div className={`stepper-item ${currentStep >= 2 ? "active" : ""}`}>2</div>
        <div className={`stepper-item ${currentStep >= 3 ? "active" : ""}`}>3</div>
      </div>
      {submitMessage && (
        <div className={`message ${submitMessage.startsWith("success") ? "success" : "error"}`}>
          {submitMessage.split(":")[1]}
        </div>
      )}

      {currentStep === 1 && (
        <>
          <h4 className="form-step-title">Paso 1: Campos de identidad básica</h4>
          <div className="form-row">
            <div className="form-group"><label>Cuánto necesitás *</label><input type="number" name="loanAmount" value={formData.loanAmount} onChange={handleChange} required min="100" placeholder="5000" /></div>
            <div className="form-group"><label>Cédula *</label><input type="text" name="nationalId" value={formData.nationalId} onChange={handleChange} onBlur={handleNationalIdBlur} required placeholder="1.234.567-8" inputMode="numeric" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Nombre *</label><input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="Nombre" /></div>
            <div className="form-group"><label>Apellido *</label><input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Apellido" /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Fecha de Nacimiento *</label>
              <input
                type="text"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                required
                placeholder="DD/MM/AAAA"
                inputMode="numeric"
              />
            </div>
          </div>
        </>
      )}

      {currentStep === 2 && (
        <>
          <h4 className="form-step-title">Paso 2: Datos de contacto y validación operativa</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Celular *</label>
              <div className="phone-wrapper">
                <span className="phone-prefix">+598</span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="99 123 456"
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="email@gmail.com"
                list="email-suggestions"
              />
              <datalist id="email-suggestions">
                <option value="usuario@gmail.com" />
                <option value="usuario@outlook.com" />
              </datalist>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Sexo *</label>
              <div className="gender-grid">
                <label className={`gender-option ${formData.gender === "femenino" ? "selected" : ""}`}>
                  <input type="radio" name="gender" value="femenino" checked={formData.gender === "femenino"} onChange={handleChange} required />
                  Femenino
                </label>
                <label className={`gender-option ${formData.gender === "masculino" ? "selected" : ""}`}>
                  <input type="radio" name="gender" value="masculino" checked={formData.gender === "masculino"} onChange={handleChange} required />
                  Masculino
                </label>
              </div>
            </div>
          </div>
        </>
      )}

      {currentStep === 3 && (
        <>
          <h4 className="form-step-title">Paso 3: Datos de identidad de la empresa</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Razón Social</label>
              <input type="text" name="companyLegalName" value={formData.companyLegalName} onChange={handleChange} placeholder="Razón social" />
            </div>
            <div className="form-group">
              <label>Nombre Fantasía</label>
              <input type="text" name="companyTradeName" value={formData.companyTradeName} onChange={handleChange} placeholder="Nombre fantasía" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>RUT de la Empresa</label>
              <input
                type="text"
                name="companyRut"
                value={formData.companyRut}
                onChange={handleChange}
                onBlur={handleCompanyRutBlur}
                placeholder="12.345.678/901-2"
                inputMode="numeric"
              />
            </div>
            <div className="form-group">
              <label>Antigüedad de la Empresa</label>
              <select name="companyAntiquity" value={formData.companyAntiquity} onChange={handleChange}>
                <option value="">Seleccione...</option>
                <option value="less-than-1-year">Menos de 1 año</option>
                <option value="between-1-and-3-years">1 a 3 años</option>
                <option value="more-than-3-years">Más de 3 años</option>
              </select>
            </div>
          </div>
        </>
      )}

      <div className="form-navigation">
        {currentStep > 1 ? (
          <button type="button" className="btn-step btn-step-secondary" onClick={handlePreviousStep}>
            Volver
          </button>
        ) : (
          <span />
        )}
        {currentStep < TOTAL_STEPS ? (
          <button type="button" className="btn-step btn-step-primary" onClick={handleNextStep}>
            Avanzar
          </button>
        ) : (
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? "Enviando..." : "Analizar mi Perfil"}
          </button>
        )}
      </div>
    </form>
  );
};

export default LoanForm;
